import React, { useState } from 'react';
import { X, HelpCircle } from 'react-feather';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { supportAPI } from '../services/api';
import { useResponsive } from '../hooks/useResponsive';
import Modal from './Modal';
import { Field, TextInput, TextArea, fieldAria } from './ui';

interface ContactSupportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ContactSupportModal: React.FC<ContactSupportModalProps> = ({ isOpen, onClose }) => {
  const { user, sessionToken } = useAuth();
  const toast = useToast();
  const { isMobile } = useResponsive();
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [subjectError, setSubjectError] = useState('');
  const [messageError, setMessageError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = (): boolean => {
    let valid = true;

    const trimmedSubject = subject.trim();
    const trimmedMessage = message.trim();

    if (!trimmedSubject) {
      setSubjectError('Subject is required');
      valid = false;
    } else if (trimmedSubject.length < 5) {
      setSubjectError('Subject must be at least 5 characters');
      valid = false;
    } else if (trimmedSubject.length > 100) {
      setSubjectError('Subject must be 100 characters or less');
      valid = false;
    } else {
      setSubjectError('');
    }

    if (!trimmedMessage) {
      setMessageError('Message is required');
      valid = false;
    } else if (trimmedMessage.length < 20) {
      setMessageError('Message must be at least 20 characters');
      valid = false;
    } else if (trimmedMessage.length > 2000) {
      setMessageError('Message must be 2000 characters or less');
      valid = false;
    } else {
      setMessageError('');
    }

    return valid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;
    if (!sessionToken) {
      toast.error('You must be logged in to submit a support request');
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await supportAPI.contactSupport(sessionToken, {
        name: user?.name || user?.email || 'Unknown User',
        email: user?.email || 'unknown@email.com',
        subject: subject.trim(),
        message: message.trim(),
      });

      if (result.success) {
        toast.success('Thank you for contacting us! We\'ll get back to you as soon as possible.');
        
        // Reset form and close
        setSubject('');
        setMessage('');
        setSubjectError('');
        setMessageError('');
        onClose();
      } else {
        toast.error('Failed to submit support request. Please try again later.');
      }

    } catch (error: any) {
      console.error('Support request error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to submit support request. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    setSubject('');
    setMessage('');
    setSubjectError('');
    setMessageError('');
    onClose();
  };

  if (!isOpen) return null;

  const isDirty = subject.trim().length > 0 || message.trim().length > 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      ariaLabel="Contact Support"
      isDirty={isDirty}
      disableClose={isSubmitting}
      zIndex={10001}
      contentStyle={{
        width: isMobile ? 'calc(100vw - 32px)' : '100%',
        maxWidth: isMobile ? 'calc(100vw - 32px)' : '600px',
        maxHeight: isMobile ? 'calc(100vh - 100px)' : '90vh',
        overflow: 'hidden',
        overflowY: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
        {/* Header */}
        <div
          style={{
            padding: isMobile ? '16px' : '24px 32px',
            borderBottom: '1px solid #E5E7EB',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <h2
            style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#1F2937',
              margin: 0,
            }}
          >
            Contact Support
          </h2>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              color: isSubmitting ? '#9CA3AF' : '#6B7280',
              opacity: isSubmitting ? 0.5 : 1,
            }}
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '32px',
          }}
        >
          {/* Header Section */}
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <div
              style={{
                width: '80px',
                height: '80px',
                borderRadius: '40px',
                background: 'rgba(249, 85, 13, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
              }}
            >
              <HelpCircle size={40} color="#f9550d" />
            </div>
            <h3
              style={{
                fontSize: '24px',
                fontWeight: '700',
                color: '#1F2937',
                margin: '0 0 8px 0',
              }}
            >
              How can we help?
            </h3>
            <p
              style={{
                fontSize: '16px',
                color: '#6B7280',
                lineHeight: '24px',
                margin: 0,
              }}
            >
              We typically respond within 24 hours. Please provide as much detail as possible.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            {/* Field owns the label/error wiring now, so the message the user
                has to read is tied to the box it belongs to and announced when
                it appears. The onFocus/onBlur pair that painted an orange
                border is gone with the inline `outline: none` that was
                cancelling the real focus ring. */}
            <Field
              id="support-subject"
              label="Subject"
              required
              error={subjectError || null}
              style={{ marginBottom: '24px' }}
            >
              <TextInput
                {...fieldAria('support-subject', { error: subjectError || null })}
                type="text"
                value={subject}
                onChange={(e) => {
                  setSubject(e.target.value);
                  setSubjectError('');
                }}
                disabled={isSubmitting}
                invalid={!!subjectError}
                placeholder="Brief description of your issue or question"
                maxLength={100}
                style={{
                  padding: '14px 16px',
                  borderRadius: '12px',
                  fontSize: '16px',
                }}
              />
            </Field>

            {/* The 2000-character counter moves up beside the label, which is
                what Field's `accessory` slot is for — it was below the box
                sharing a row with the error, and the two competed for it. */}
            <Field
              id="support-message"
              label="Message"
              required
              error={messageError || null}
              accessory={
                <span style={{ fontSize: '14px', color: '#6B7280' }}>
                  {message.length}/2000
                </span>
              }
              style={{ marginBottom: '24px' }}
            >
              <TextArea
                {...fieldAria('support-message', { error: messageError || null })}
                value={message}
                onChange={(e) => {
                  setMessage(e.target.value);
                  setMessageError('');
                }}
                disabled={isSubmitting}
                invalid={!!messageError}
                placeholder="Please provide as much detail as possible about your issue or question..."
                maxLength={2000}
                rows={6}
                style={{
                  padding: '14px 16px',
                  borderRadius: '12px',
                  fontSize: '16px',
                  minHeight: '120px',
                }}
              />
            </Field>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                width: '100%',
                padding: '16px 24px',
                fontSize: '16px',
                fontWeight: '600',
                color: 'white',
                background: isSubmitting ? '#9CA3AF' : 'linear-gradient(135deg, #f9550d 0%, #ff6b2c 100%)',
                border: 'none',
                borderRadius: '12px',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                fontFamily: 'Manrope, sans-serif',
                transition: 'all 0.2s ease',
                boxShadow: isSubmitting ? 'none' : '0 4px 12px rgba(249, 85, 13, 0.3)',
              }}
              onMouseEnter={(e) => {
                if (!isSubmitting) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(249, 85, 13, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                if (!isSubmitting) {
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(249, 85, 13, 0.3)';
                }
              }}
            >
              {isSubmitting ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  <div
                    style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      borderTop: '2px solid white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                    }}
                  />
                  Submitting...
                </div>
              ) : (
                'Submit Support Request'
              )}
            </button>
          </form>
        </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </Modal>
  );
};

export default ContactSupportModal;
